import { endpoints } from '../config/endpoints';
import { getUserId } from './userUtils';
import { fetchUserSubscription } from './subscriptionUtils';

// Cache for media usage data to avoid repeated API calls
let usageCache = null;
let cacheExpiry = 0;
const CACHE_DURATION = 2 * 60 * 1000; // 2 minutes (shorter than subscription cache)

/**
 * Get user's subscription tier
 * @param {Object} user - AWS Amplify user object
 * @returns {Promise<string>} Subscription tier ("free", "0" for Basic, "1" for Pro)
 */
export const getUserSubscriptionTier = async (user) => {
  try {
    const subscription = await fetchUserSubscription(user);
    
    if (!subscription) {
      return "free"; // Default to Free Plan
    }
    
    // Use the subscription_tier field directly
    return subscription.subscription_tier || "free";
  } catch (error) {
    console.error('Error getting subscription tier:', error);
    return "free"; // Default to Free Plan on error
  }
};

/**
 * Check if user can make a media request
 * @param {Object} user - AWS Amplify user object
 * @param {string} mediaType - Type of media ("picture" or "video")
 * @returns {Promise<Object>} Usage check result
 */
export const checkMediaUsageLimits = async (user, mediaType = "picture") => {
  try {
    const userId = getUserId(user);
    if (!userId) {
      return {
        canRequest: false,
        error: 'No user ID available',
        limitInfo: {}
      };
    }

    const subscriptionTier = await getUserSubscriptionTier(user);

    const response = await fetch(endpoints.api.mediaUsage, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      mode: 'cors',
      body: JSON.stringify({
        action: 'check_usage',
        user_id: userId,
        media_type: mediaType,
        subscription_tier: subscriptionTier
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    
    if (result.success) {
      return {
        canRequest: result.data.can_request,
        limitInfo: result.data,
        error: null
      };
    } else {
      return {
        canRequest: false,
        error: result.error || 'Unknown error',
        limitInfo: {}
      };
    }
  } catch (error) {
    console.error('Error checking media usage limits:', error);
    return {
      canRequest: false,
      error: error.message,
      limitInfo: {}
    };
  }
};

/**
 * Record a successful media request
 * @param {Object} user - AWS Amplify user object
 * @param {string} mediaType - Type of media ("picture" or "video")
 * @returns {Promise<boolean>} Success status
 */
export const recordMediaUsage = async (user, mediaType = "picture") => {
  try {
    const userId = getUserId(user);
    if (!userId) {
      console.error('No user ID available for recording usage');
      return false;
    }

    const response = await fetch(endpoints.api.mediaUsage, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      mode: 'cors',
      body: JSON.stringify({
        action: 'record_usage',
        user_id: userId,
        media_type: mediaType,
        timestamp: new Date().toISOString()
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    
    if (result.success) {
      // Clear usage cache to ensure fresh data on next fetch
      clearUsageCache();
      return true;
    } else {
      console.error('Failed to record media usage:', result.error);
      return false;
    }
  } catch (error) {
    console.error('Error recording media usage:', error);
    return false;
  }
};

/**
 * Get user's usage statistics
 * @param {Object} user - AWS Amplify user object
 * @param {string} monthYear - Optional month in YYYY-MM format
 * @returns {Promise<Object>} Usage statistics
 */
export const getUsageStats = async (user, monthYear = null) => {
  try {
    const userId = getUserId(user);
    if (!userId) {
      return {
        success: false,
        error: 'No user ID available',
        data: {}
      };
    }

    // Check cache first
    const now = Date.now();
    if (usageCache && now < cacheExpiry && !monthYear) {
      console.log('Using cached usage data');
      return usageCache;
    }

    const response = await fetch(endpoints.api.mediaUsage, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      mode: 'cors',
      body: JSON.stringify({
        action: 'get_stats',
        user_id: userId,
        month_year: monthYear
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    
    if (result.success) {
      // Cache current month data only
      if (!monthYear) {
        usageCache = result;
        cacheExpiry = now + CACHE_DURATION;
      }
      
      return result;
    } else {
      return {
        success: false,
        error: result.error || 'Unknown error',
        data: {}
      };
    }
  } catch (error) {
    console.error('Error getting usage stats:', error);
    return {
      success: false,
      error: error.message,
      data: {}
    };
  }
};

/**
 * Get subscription limits information
 * @param {string} subscriptionTier - Subscription tier ("free", "0", or "1")
 * @returns {Object} Subscription limits
 */
export const getSubscriptionLimits = (subscriptionTier) => {
  const limits = {
    "free": {
      name: "Free Plan",
      monthly_pictures: 0,
      monthly_videos: 0,
    },
    "0": {
      name: "Basic Plan",
      monthly_pictures: 10,
      monthly_videos: 5,
    },
    "1": {
      name: "Pro Plan",
      monthly_pictures: 100,
      monthly_videos: 100,
    }
  };
  
  return limits[subscriptionTier] || limits["free"];
};

/**
 * Calculate usage percentage for progress indicators
 * @param {number} current - Current usage count
 * @param {number} limit - Maximum allowed
 * @returns {number} Percentage (0-100)
 */
export const calculateUsagePercentage = (current, limit) => {
  if (limit === 0) return 0;
  return Math.min(100, Math.round((current / limit) * 100));
};

/**
 * Check if user is approaching usage limit
 * @param {number} current - Current usage count
 * @param {number} limit - Maximum allowed
 * @param {number} threshold - Warning threshold percentage (default 80%)
 * @returns {boolean} Whether user is approaching limit
 */
export const isApproachingLimit = (current, limit, threshold = 80) => {
  const percentage = calculateUsagePercentage(current, limit);
  return percentage >= threshold;
};

/**
 * Check if a limit is effectively unlimited (kept for backward compatibility)
 * @param {number} limit - The limit value
 * @returns {boolean} Whether the limit should be treated as unlimited
 */
export const isUnlimitedPlan = (limit) => {
  // In the three-tier system, no tier is truly unlimited
  // But we can consider high limits as "generous"
  return limit >= 100;
};

/**
 * Get user-friendly usage status message
 * @param {Object} usageData - Usage data from API
 * @param {string} mediaType - Type of media
 * @returns {Object} Status message and type
 */
export const getUsageStatusMessage = (usageData, mediaType = "picture") => {
  const { current_count = 0, limit = 0, can_request = false, subscription_tier = "free" } = usageData;
  const planName = getSubscriptionLimits(subscription_tier).name;

  // Free plan users have no media access
  if (subscription_tier === "free") {
    return {
      message: `Upgrade to Basic Plan to get ${mediaType} access`,
      type: "free_tier",
      severity: "info"
    };
  }

  // No requests allowed
  if (!can_request) {
    return {
      message: `You've used all ${limit} ${mediaType}${limit !== 1 ? 's' : ''} for this month (${planName})`,
      type: "limit_exceeded",
      severity: "error"
    };
  }

  const remaining = Math.max(0, limit - current_count);
  const percentage = calculateUsagePercentage(current_count, limit);

  if (percentage >= 80) {
    return {
      message: `${remaining} ${mediaType}${remaining !== 1 ? 's' : ''} remaining this month (${current_count}/${limit} used)`,
      type: "approaching_limit",
      severity: "warning"
    };
  }

  return {
    message: `${remaining} ${mediaType}${remaining !== 1 ? 's' : ''} remaining this month (${planName})`,
    type: "normal",
    severity: "info"
  };
};

/**
 * Clear usage cache (useful after subscription changes or manual refresh)
 */
export const clearUsageCache = () => {
  usageCache = null;
  cacheExpiry = 0;
  console.log('Usage cache cleared');
};

/**
 * Get upgrade message for limit exceeded scenarios
 * @param {string} currentTier - Current subscription tier
 * @param {string} mediaType - Type of media
 * @returns {Object} Upgrade suggestion
 */
export const getUpgradeMessage = (currentTier, mediaType = "picture") => {
  switch (currentTier) {
    case "free":
      return {
        message: `Upgrade to Basic Plan to get ${mediaType} access!`,
        buttonText: "Upgrade to Basic",
        tier: "0"
      };
    case "0":
      return {
        message: `Upgrade to Pro Plan for more ${mediaType}s!`,
        buttonText: "Upgrade to Pro", 
        tier: "1"
      };
    case "1":
      return {
        message: "You've reached your monthly limit. Limit resets next month.",
        buttonText: "Manage Subscription",
        tier: "1"
      };
    default:
      return {
        message: `Upgrade to get ${mediaType} access!`,
        buttonText: "Upgrade Plan",
        tier: "0"
      };
  }
};

/**
 * Detect media type from user message
 * @param {string} message - User's message
 * @returns {string} Detected media type ("picture", "video", or "none")
 */
export const detectMediaTypeFromMessage = (message) => {
  if (!message || typeof message !== 'string') return "none";
  
  const lowerMessage = message.toLowerCase();
  
  // Picture keywords (English, Spanish, Japanese)
  const pictureKeywords = [
    "selfie", "photo of you", "picture of you", "send me a photo",
    "your photo", "imagen tuya", "foto tuya", "自撮り", "写真",
    "send me a selfie", "can you send me a selfie", "dame una selfie",
    "mandame una pic", "send me a picture"
  ];
  
  // Video keywords (for future use)
  const videoKeywords = [
    "video", "send me a video", "video of you", "record a video",
    "video tuyo", "graba un video", "ビデオ", "動画"
  ];
  
  if (pictureKeywords.some(keyword => lowerMessage.includes(keyword))) {
    return "picture";
  }
  
  if (videoKeywords.some(keyword => lowerMessage.includes(keyword))) {
    return "video";
  }
  
  return "none";
};